import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAgent } from '../../contexts/AgentContext';
import ErrorState from '../common/ErrorState';
import { GetAgentDashboardStatsResponse } from '@dreamai/types';
import api from '../../api';
import ManagerStats from './ManagerStats';
import AgentUpdate from './AgentUpdate';

// Per-agent state for dashboard stats
interface AgentDashboardState {
  stats: GetAgentDashboardStatsResponse | null;
  loading: boolean;
  error: string | null;
}

const ConversationalDashboard: React.FC = () => {
  const { agents, loading, error, loadAgents } = useAgent();
  const { getAccessTokenSilently } = useAuth0();

  // Stats per agent, keyed by agent ID
  const [agentStats, setAgentStats] = useState<
    Record<string, AgentDashboardState>
  >({});

  // Load agents when component mounts
  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  // Load dashboard stats for all agents when agents are loaded
  useEffect(() => {
    if (agents.length === 0) return;

    const loadAllStats = async () => {
      const token = await getAccessTokenSilently();

      // Initialize loading state for all agents
      const initialState: Record<string, AgentDashboardState> = {};
      agents.forEach((agent) => {
        initialState[agent.id] = { stats: null, loading: true, error: null };
      });
      setAgentStats(initialState);

      // Load stats for each agent
      for (const agent of agents) {
        try {
          const response = await api.get<GetAgentDashboardStatsResponse>(
            `/agent-dashboard/${agent.id}/stats?days=7`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );
          setAgentStats((prev) => ({
            ...prev,
            [agent.id]: { stats: response.data, loading: false, error: null },
          }));
        } catch (err) {
          console.error(`Error loading stats for agent ${agent.id}:`, err);
          setAgentStats((prev) => ({
            ...prev,
            [agent.id]: {
              stats: null,
              loading: false,
              error: 'Failed to load stats',
            },
          }));
        }
      }
    };

    loadAllStats();
  }, [agents, getAccessTokenSilently]);

  // Loading state
  if (loading) {
    return (
      <div className='conversational-dashboard'>
        <div className='loading-top-bar'>
          <div className='loading-agent-ghost'>
            <div className='loading-agent-circle'></div>
          </div>
          <div className='loading-agent-ghost'>
            <div className='loading-agent-circle'></div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className='conversational-dashboard'>
        <div className='loading-top-bar'>
          <div className='loading-agent-ghost'>
            <div className='loading-agent-circle'></div>
          </div>
          <div className='loading-agent-ghost'>
            <div className='loading-agent-circle'></div>
          </div>
        </div>
        <div className='error-agent-tab-wrapper'>
          <ErrorState title='Unable to load agents' subtitle='fol' />
        </div>
      </div>
    );
  }

  // No agents state
  if (agents.length === 0) {
    return (
      <div className='conversational-dashboard'>
        <div className='loading-top-bar'>
          <div className='loading-agent-ghost'>
            <div className='loading-agent-circle'></div>
          </div>
          <div className='loading-agent-ghost'>
            <div className='loading-agent-circle'></div>
          </div>
        </div>
        <div className='error-agent-tab-wrapper'>
          <ErrorState
            title='No agents available'
            subtitle='No agents found for your account.'
          />
        </div>
      </div>
    );
  }

  // Success state - show dashboard for each agent
  return (
    <div className='conversational-dashboard inset-white-backed'>
      <div className='dashboard-header'>Dashboard</div>
      <div className='dashboard-lower'>
        {agents.map((agent) => {
          const stats = agentStats[agent.id];
          return (
            <div key={agent.id} className='dashboard-main'>
              <div className='dashboard-left'>
                {agent.image_url && (
                  <img
                    src={agent.image_url}
                    alt={agent.name || agent.agent_type}
                    className='dreamie-large'
                  />
                )}
              </div>

              <div className='dashboard-right'>
                <h4>{agent.nickname || agent.name}</h4>

                <div className='important-numbers'>
                  <div className='numbers-left'>
                    <h2>How're things going this week?</h2>
                    {stats?.loading && <StatLoadingView />}
                    {stats?.error && <p className='error'>{stats.error}</p>}
                    {!stats?.loading &&
                      stats?.stats?.stats &&
                      stats?.stats?.display_metadata && (
                        <ManagerStats
                          stats={stats.stats.stats}
                          displayMetadata={stats.stats.display_metadata}
                        />
                      )}
                    {!stats?.loading &&
                      (!stats?.stats?.stats ||
                        !stats?.stats?.display_metadata) && (
                        <div className='stats-empty-state'>
                          It's really hard to put a number on how busy I've
                          been.
                        </div>
                      )}
                  </div>
                  <div className='update-area'>
                    <AgentUpdate
                      agentId={agent.id}
                      agentName={
                        agent.nickname || agent.name || agent.agent_type
                      }
                      predominantColour={agent.predominant_colour}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const StatLoadingView = () => (
  <div className='stats-loading-wrapper'>
    <div className='stat'>
      <div className='ghost-bar'></div>
      <p>-</p>
    </div>
    <div className='stat'>
      <div className='ghost-bar'></div>
      <p>-</p>
    </div>
    <div className='stat fake'></div>
    <div className='stat fake'></div>
  </div>
);
export default ConversationalDashboard;
